import type { AlgoliaCompanyRecord } from '../../types/company-search-record';

export interface CompanyProfileDataTable {
  caption: string;
  headers: string[];
  rows: string[][];
}

export interface CompanyProfileTableColumnLabels {
  metricColumn: string;
  valueColumn: string;
  categoryColumn: string;
  countColumn: string;
  monthColumn: string;
  jobPostingsColumn: string;
  captions: {
    overview: string;
    employerScore: string;
    salary: string;
    workModel: string;
    roles: string;
    locations: string;
    languages: string;
    hiringByMonth: string;
    financials: string;
    registry: string;
  };
}

function sortMonthKeys(keys: string[]): string[] {
  return [...keys].sort((a, b) => a.localeCompare(b));
}

/**
 * Machine-readable HTML table payloads (salary, distributions, registry) for crawlers and SR users.
 */
export function buildCompanyProfileDataTables(
  company: AlgoliaCompanyRecord,
  labels: CompanyProfileTableColumnLabels,
): CompanyProfileDataTable[] {
  const { metricColumn, valueColumn, categoryColumn, countColumn, monthColumn, jobPostingsColumn, captions } =
    labels;
  const tables: CompanyProfileDataTable[] = [];

  const overviewRows: string[][] = [];
  overviewRows.push(['objectID', company.objectID]);
  if (company.companyName != null) overviewRows.push(['companyName', String(company.companyName)]);
  if (company.industryEn) overviewRows.push(['industryEn', company.industryEn]);
  if (company.industryFi) overviewRows.push(['industryFi', company.industryFi]);
  if (company.industrySv) overviewRows.push(['industrySv', company.industrySv]);
  if (company.registryCity) overviewRows.push(['registryCity', company.registryCity]);
  if (company.registryCountry) overviewRows.push(['registryCountry', company.registryCountry]);
  if (company.companyHeadCountMin != null) {
    overviewRows.push(['companyHeadCountMin', String(company.companyHeadCountMin)]);
  }
  if (company.companyHeadCountMax != null) {
    overviewRows.push(['companyHeadCountMax', String(company.companyHeadCountMax)]);
  }
  if (company.hiringTrend) overviewRows.push(['hiringTrend', company.hiringTrend]);
  if (company.employerScore != null) overviewRows.push(['employerScore', String(company.employerScore)]);
  if (company.remotenessRemotePct != null) {
    overviewRows.push(['remotenessRemotePct', String(company.remotenessRemotePct)]);
  }
  if (company.remotenessHybridPct != null) {
    overviewRows.push(['remotenessHybridPct', String(company.remotenessHybridPct)]);
  }
  if (overviewRows.length > 0) {
    tables.push({
      caption: captions.overview,
      headers: [metricColumn, valueColumn],
      rows: overviewRows,
    });
  }

  const subScores: [string, number | undefined][] = [
    ['payScore', company.payScore],
    ['flexibilityScore', company.flexibilityScore],
    ['stabilityScore', company.stabilityScore],
    ['growthScore', company.growthScore],
  ];
  const scoreRows = subScores
    .filter(([, v]) => v != null && !Number.isNaN(v))
    .map(([k, v]) => [k, String(v)]);
  if (scoreRows.length > 0) {
    tables.push({
      caption: captions.employerScore,
      headers: [metricColumn, valueColumn],
      rows: scoreRows,
    });
  }

  const salaryRows: string[][] = [];
  if (company.medianSalaryMin != null) salaryRows.push(['medianSalaryMin', String(company.medianSalaryMin)]);
  if (company.medianSalaryMax != null) salaryRows.push(['medianSalaryMax', String(company.medianSalaryMax)]);
  if (company.salaryPercentile25 != null) {
    salaryRows.push(['salaryPercentile25', String(company.salaryPercentile25)]);
  }
  if (company.salaryPercentile75 != null) {
    salaryRows.push(['salaryPercentile75', String(company.salaryPercentile75)]);
  }
  if (company.salarySampleSize != null) {
    salaryRows.push(['salarySampleSize', String(company.salarySampleSize)]);
  }
  if (salaryRows.length > 0) {
    tables.push({
      caption: captions.salary,
      headers: [metricColumn, valueColumn],
      rows: salaryRows,
    });
  }

  const rem = company.remotenessDistribution;
  if (rem) {
    const total = rem.remote + rem.hybrid + rem.onsite;
    tables.push({
      caption: captions.workModel,
      headers: [categoryColumn, countColumn],
      rows: [
        ['Remote', String(rem.remote)],
        ['Hybrid', String(rem.hybrid)],
        ['Onsite', String(rem.onsite)],
        ['total', String(total)],
      ],
    });
  }

  const pushDistribution = (
    caption: string,
    dist: Record<string, number> | undefined,
    maxRows = 40,
  ) => {
    if (!dist || Object.keys(dist).length === 0) return;
    const rows = Object.entries(dist)
      .sort((a, b) => b[1] - a[1])
      .slice(0, maxRows)
      .map(([k, v]) => [k, String(v)]);
    tables.push({
      caption,
      headers: [categoryColumn, countColumn],
      rows,
    });
  };

  pushDistribution(captions.roles, company.roleDistribution);
  pushDistribution(captions.locations, company.locationDistribution);
  pushDistribution(captions.languages, company.languageRequirements);

  const monthly = company.companyMonthlyCounts;
  if (monthly && Object.keys(monthly).length > 0) {
    const rows = sortMonthKeys(Object.keys(monthly)).map((k) => [k, String(monthly[k] ?? '')]);
    tables.push({
      caption: captions.hiringByMonth,
      headers: [monthColumn, jobPostingsColumn],
      rows,
    });
  }

  const finRows: string[][] = [];
  if (company.revenue != null) finRows.push(['revenue', String(company.revenue)]);
  if (company.profit != null) finRows.push(['profit', String(company.profit)]);
  if (company.profitMargin != null) finRows.push(['profitMargin', String(company.profitMargin)]);
  if (finRows.length > 0) {
    tables.push({
      caption: captions.financials,
      headers: [metricColumn, valueColumn],
      rows: finRows,
    });
  }

  const regRows: string[][] = [];
  if (company.registryBusinessId) regRows.push(['registryBusinessId', company.registryBusinessId]);
  if (company.registryStatus) regRows.push(['registryStatus', company.registryStatus]);
  if (company.registryRegistrationDate) {
    regRows.push(['registryRegistrationDate', company.registryRegistrationDate]);
  }
  if (company.registryMainBusinessLineDescriptionEn) {
    regRows.push(['registryMainBusinessLineDescriptionEn', company.registryMainBusinessLineDescriptionEn]);
  }
  if (company.registryMainBusinessLineDescriptionFi) {
    regRows.push(['registryMainBusinessLineDescriptionFi', company.registryMainBusinessLineDescriptionFi]);
  }
  if (company.registryMainBusinessLineDescriptionSv) {
    regRows.push(['registryMainBusinessLineDescriptionSv', company.registryMainBusinessLineDescriptionSv]);
  }
  if (regRows.length > 0) {
    tables.push({
      caption: captions.registry,
      headers: [metricColumn, valueColumn],
      rows: regRows,
    });
  }

  return tables;
}
